import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  type GrantChanges,
  type RemoveAllFollowUp,
  useGrantChanges,
} from "permissions/_views/grant-row/use-grant-changes";
import { recordResource } from "permissions/_views/records/record-grants-model";
import type { FieldAccessValues } from "permissions/_views/people/field-access-dialog";
import type { GrantLevel } from "permissions/_views/wording/permission-wording";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";
import type { AnyResourceJSON } from "permissions/resources/permission-resource-types";
import { useCallback } from "react";

export { REMOVE_ALL_KEY, type RemoveAllFollowUp, withFields } from "permissions/_views/grant-row/use-grant-changes";

/** The record a per-record grant is on, and for one proposal or one work agreement the fields it confers. */
export type GrantTarget = Readonly<{ id: string; fields?: FieldAccessValues }>;

/** The resource of a new grant of a per-record `kind` on `target`, with the fields it names. */
export function targetResource(kind: PermissionKind, level: GrantLevel | undefined, target: GrantTarget) {
  const resource = recordResource(kind, level, target.id);
  if (!target.fields) return resource;
  return resourceFromJSON({ ...(resource.toJSON() as Record<string, unknown>), fields: { ...target.fields } } as AnyResourceJSON);
}

export type PersonGrantChanges = Omit<GrantChanges, "grant" | "removeAll"> &
  Readonly<{
    /** Grants a standing kind, or with `target` a per-record kind on that record. */
    grant: (kind: PermissionKind, level?: GrantLevel, target?: GrantTarget) => Promise<boolean>;
    /** "Remove all access…" for this person (#6176); see `GrantChanges.removeAll`. */
    removeAll: (stopSignIn: boolean, after?: RemoveAllFollowUp) => Promise<boolean>;
  }>;

/**
 * The person page's writes: `useGrantChanges` for one person, whose name every change is told by,
 * to whom every Grant goes, and whose access "Remove all access…" removes.
 */
export default function usePersonGrantChanges(
  person: Readonly<{ userId: Guid; name: string; legalEntityId: Guid }>,
  viewerUserId: Guid,
): PersonGrantChanges {
  const { userId, name, legalEntityId } = person;
  const nameOf = useCallback(() => name, [name]);
  const changes = useGrantChanges({ legalEntityId, nameOf }, viewerUserId);
  const grantTo = changes.grant;
  const grant = useCallback(
    (kind: PermissionKind, level?: GrantLevel, target?: GrantTarget) =>
      grantTo(kind, level, userId, target ? targetResource(kind, level, target) : undefined),
    [grantTo, userId],
  );
  const removeAllOf = changes.removeAll;
  const removeAll = useCallback(
    (stopSignIn: boolean, after?: RemoveAllFollowUp) => removeAllOf(userId, stopSignIn, after),
    [removeAllOf, userId],
  );
  return { ...changes, grant, removeAll };
}
